import React, { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';

import { Space, Form, Button, DatePicker, Switch, Alert } from 'antd';
import { ApplicationUIContext, MoneyInput, Drawer } from '@yuhuinc/ui-library';

import { PlusOutlined } from '@ant-design/icons';

import { getTranslatedString } from 'helpers/i18n';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px ${({ isMobile }) => (isMobile ? '16px' : '24px')};
`;

const StyledNewSpace = styled(Space)`
  width: 100%;

  > * {
    flex: 1;
  }

  .ant-input-number,
  .ant-input-number-group-wrapper {
    width: 100%;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  &.ant-picker {
    width: 100%;
  }

  ${(props) =>
    css`
      input:not(.ant-input) {
        font-size: 14px !important;
      }
    `}
`;

const InlineFormItem = styled(Form.Item)`
  flex-direction: row-reverse !important;
  align-items: baseline;
  gap: 8px;
  width: fit-content;
`;

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const StyledAlert = styled(Alert)`
  .ant-alert-message {
    font-size: ${({ isMobile }) => (isMobile ? '20px' : '16px')};
  }

  .ant-alert-description {
    font-size: ${({ isMobile }) => (isMobile ? '16px' : '14px')};
  }

  .ant-alert-description p {
    margin-bottom: 8px;
  }

  .ant-alert-description .ant-btn.ant-btn-link {
    padding: 0;
  }
`;

const DepositError = ({ message, description, isMobile }) => {
  return (
    <StyledAlert
      message={message}
      description={description}
      type="error"
      showIcon
      isMobile={isMobile}
    />
  );
};

const AddDeposit = ({ initialValues = {}, closeDrawer, onAdd, isEdit = false }) => {
  const [depositError, setDepositError] = useState(false);
  const { isMobile } = useContext(ApplicationUIContext);
  const [form] = Form.useForm();

  const onFinish = (value) => {
    onAdd(value);
    closeDrawer();
  };

  function onFinishFailed({ errorFields }) {
    if (errorFields.length > 0) {
      setDepositError({
        description: getTranslatedString('errorsOnThisPage'),
      });
    }
  }

  return (
    <StyledContainer isMobile={isMobile}>
      {depositError && <DepositError {...depositError} isMobile={isMobile} />}
      <Form
        layout="vertical"
        size={isMobile && 'large'}
        form={form}
        name="create-deposit"
        scrollToFirstError
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={initialValues}
      >
        <StyledNewSpace direction="vertical" size="middle">
          <StyledNewSpace
            align="start"
            direction={isMobile ? 'vertical' : 'horizontal'}
            size={isMobile ? 16 : 10}
          >
            <Form.Item
              label={getTranslatedString(
                'landlordPortal.applicationManagement.inviteLeadToApply.deposits.amount'
              )}
              name={'amount'}
              rules={[requiredField]}
            >
              <MoneyInput />
            </Form.Item>
            <Form.Item
              label={getTranslatedString(
                'landlordPortal.applicationManagement.inviteLeadToApply.deposits.date'
              )}
              name={'date'}
              rules={[requiredField]}
            >
              <StyledDatePicker />
            </Form.Item>
          </StyledNewSpace>
          <Space>
            <InlineFormItem
              label={getTranslatedString(
                'landlordPortal.applicationManagement.inviteLeadToApply.deposits.depositPaidInPerson'
              )}
              valuePropName="checked"
              name={'paid_in_person'}
            >
              <Switch size={!isMobile && 'small'} />
            </InlineFormItem>
          </Space>
          <Space>
            <Button type="primary" htmlType="submit">
              {isEdit ? getTranslatedString('edit') : getTranslatedString('add')}
            </Button>
            <Button htmlType="button" onClick={closeDrawer}>
              {getTranslatedString('cancel')}
            </Button>
          </Space>
        </StyledNewSpace>
      </Form>
    </StyledContainer>
  );
};

AddDeposit.propTypes = {
  initialValues: PropTypes.object,
  closeDrawer: PropTypes.func,
  onAdd: PropTypes.func,
  isEdit: PropTypes.bool,
};

export default AddDeposit;
